"use client";
import { useState, useEffect } from "react";
import { useParams, usePathname } from "next/navigation";
import { useFormik, type FormikErrors } from "formik";
import Image from "next/image";
import { MdPhoneIphone } from "react-icons/md";
import {
	DrawerContent,
	DrawerRoot,
	DrawerTrigger,
	DrawerBody,
	DrawerHeader,
	DrawerCloseTrigger,
} from "../ui/drawer";
import { requestCallback } from "@/app/product/_actions/callback.actions";
import {
	getOtp,
	verifyOtpForMobile,
} from "@/app/product/_actions/user.actions";
import { useAppData } from "@/store/appStore";
import { validateOtp, validatePhone } from "@/lib/utils";
import { clientLogger } from "@/lib/logger";
import { faroManager } from "@/lib/client/faro-manager";
import { Button } from "@/design-elements/button";
import { Input } from "@/design-elements/input";
import { OTPInput } from "@/design-elements/otp-input";
import { cn } from "@/lib/utils";
import { Turnstile } from "@marsidev/react-turnstile";
import { GTMEvent, trackEvent } from "@/lib/utils/gtm-events";

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";
const WHATSAPP_URL = process.env.NEXT_PUBLIC_WHATSAPP_URL || "";

type ComponentState = "form" | "success" | "error";
type CurrentStep = "phone" | "otp";

type FormValues = {
	phoneNumber: string;
	otp: string;
	captcha: string | null;
	message: string;
};

type ProductFormValues = {
	phoneNumber: string;
	otp: string;
	captcha: string | null;
	message: string;
};

const Callback = ({
	type = "default",
	embedded = false,
	active = true,
	source,
}: {
	type?: "foreclosure" | "default";
	embedded?: boolean;
	active?: boolean;
	source?: string;
}) => {
	const effectiveSource =
		source ?? (type === "foreclosure" ? "foreclosure" : "fab_icon");
	const [appData] = useAppData();
	const pathname = usePathname();
	const params = useParams();
	const [open, setOpen] = useState<boolean>(false);
	const [openProductReq, setOpenProductReq] = useState<boolean>(false);
	const [fabOpen, setFabOpen] = useState<boolean>(false);
	const [isNativeApp, setIsNativeApp] = useState<boolean>(false);

	useEffect(() => {
		if (typeof window !== "undefined" && window.isNativeApp) {
			setIsNativeApp(true);
		}
	}, []);

	// ── Callback form state ──────────────────────────────────────────────────
	const [componentState, setComponentState] = useState<ComponentState>("form");
	const [currentStep, setCurrentStep] = useState<CurrentStep>("phone");
	const [resendTimerActive, setResendTimerActive] = useState<boolean>(false);
	const [resendTimerCount, setResendTimerCount] = useState<number>(60);
	const [apiError, setApiError] = useState<string>("");

	// ── Product request form state ───────────────────────────────────────────
	const [productComponentState, setProductComponentState] =
		useState<ComponentState>("form");
	const [productCurrentStep, setProductCurrentStep] =
		useState<CurrentStep>("phone");
	const [productResendTimerActive, setProductResendTimerActive] =
		useState<boolean>(false);
	const [productResendTimerCount, setProductResendTimerCount] =
		useState<number>(60);
	const [productApiError, setProductApiError] = useState<string>("");

	useEffect(() => {
		clientLogger.info("Callback Login status:", {
			isLoggedIn: !!appData?.user,
		});
	}, [appData?.user]);

	// ── Callback formik ──────────────────────────────────────────────────────
	const formik = useFormik<FormValues>({
		initialValues: {
			phoneNumber: appData?.user?.mobileNo
				? appData.user.mobileNo.toString()
				: "",
			otp: "",
			message: "",
			captcha: null,
		},
		validate: (values) => {
			const errors: FormikErrors<FormValues> = {};

			if (currentStep === "phone" && !appData?.user?.mobileNo) {
				if (!validatePhone(values.phoneNumber || "")) {
					errors.phoneNumber = "Please enter a valid 10-digit mobile number.";
				}
			}

			if (currentStep === "otp") {
				if (!values.otp || !validateOtp(values.otp)) {
					errors.otp = "Please enter a valid 5-digit OTP.";
				}
			}

			return errors;
		},
		onSubmit: async (values) => {
			setApiError("");

			if (currentStep === "otp" && !values.captcha) {
				formik.setFieldError(
					"otp",
					"Please complete the captcha verification.",
				);
				return;
			}

			if (appData?.user && appData?.user?.mobileNo) {
				const phoneNumber = String(appData?.user?.mobileNo);
				faroManager.pushEvent("callback_form_submitted", {
					number: phoneNumber,
					hasMessage: !!values.message,
				});

				clientLogger.info("Requesting callback for logged-in user", {
					phoneNumber,
					userId: appData?.user?.id,
					hasMessage: !!values.message,
				});

				const response = await requestCallback({
					number: phoneNumber,
					userNotes: values.message || undefined,
				});

				if ("error" in response) {
					const errorResponse = response as ErrorState;
					clientLogger.error(
						"Callback request returned error",
						new Error(errorResponse.errorText || "Unknown error"),
						{
							errorCode: errorResponse.errorCode,
							errorText: errorResponse.errorText,
							phoneNumber,
						},
					);

					if (errorResponse.errorCode === 409) {
						trackEvent(GTMEvent.CALLBACK_REQUESTED, {
							source: effectiveSource,
						});
						clientLogger.info(
							"Callback request conflict (409) - treating as success",
							{
								phoneNumber,
							},
						);
						setComponentState("success");
					} else {
						setComponentState("error");
						setApiError(
							errorResponse.errorText ||
								"Failed to request callback. Please try again.",
						);
					}
				} else {
					clientLogger.info("Callback request successful", {
						callbackId: response?.callbackId,
						userId: response?.userId,
						phoneNumber,
					});
					trackEvent(GTMEvent.CALLBACK_REQUESTED, { source: effectiveSource });
					setComponentState("success");
				}
			} else {
				if (currentStep === "phone") {
					faroManager.pushEvent("callback_form_submitted", {
						number: values.phoneNumber,
						hasMessage: !!values.message,
					});
					await sendOtp(values.phoneNumber);
				} else {
					await handleOtpSubmit(values);
				}
			}
		},
		enableReinitialize: true,
	});

	// ── Product request formik ───────────────────────────────────────────────
	const productFormik = useFormik<ProductFormValues>({
		initialValues: {
			phoneNumber: appData?.user?.mobileNo
				? appData.user.mobileNo.toString()
				: "",
			otp: "",
			message: "",
			captcha: null,
		},
		validate: (values) => {
			const errors: FormikErrors<ProductFormValues> = {};

			if (!values.message.trim()) {
				errors.message = "Please describe the product you're looking for.";
			}

			if (productCurrentStep === "phone" && !appData?.user?.mobileNo) {
				if (!validatePhone(values.phoneNumber || "")) {
					errors.phoneNumber = "Please enter a valid 10-digit mobile number.";
				}
			}

			if (productCurrentStep === "otp") {
				if (!values.otp || !validateOtp(values.otp)) {
					errors.otp = "Please enter a valid 5-digit OTP.";
				}
			}

			return errors;
		},
		onSubmit: async (values) => {
			setProductApiError("");

			if (productCurrentStep === "otp" && !values.captcha) {
				productFormik.setFieldError(
					"otp",
					"Please complete the captcha verification.",
				);
				return;
			}

			const userNotes = `User has requested for a new product: "${values.message.trim()}"`;

			if (appData?.user && appData?.user?.mobileNo) {
				const phoneNumber = String(appData?.user?.mobileNo);
				faroManager.pushEvent("product_request_form_submitted", {
					number: phoneNumber,
				});

				const response = await requestCallback({
					number: phoneNumber,
					userNotes,
				});

				if ("error" in response) {
					const errorResponse = response as ErrorState;
					if (errorResponse.errorCode === 409) {
						trackEvent(GTMEvent.CALLBACK_REQUESTED, {
							source: "request_product",
						});
						setProductComponentState("success");
					} else {
						setProductComponentState("error");
						setProductApiError(
							errorResponse.errorText ||
								"Failed to submit request. Please try again.",
						);
					}
				} else {
					trackEvent(GTMEvent.CALLBACK_REQUESTED, {
						source: "request_product",
					});
					setProductComponentState("success");
				}
			} else {
				if (productCurrentStep === "phone") {
					faroManager.pushEvent("product_request_form_submitted", {
						number: values.phoneNumber,
					});
					await sendProductOtp(values.phoneNumber);
				} else {
					await handleProductOtpSubmit(values, userNotes);
				}
			}
		},
		enableReinitialize: true,
	});

	// ── Callback form effects ────────────────────────────────────────────────
	useEffect(() => {
		if (appData?.user?.mobileNo && currentStep === "phone") {
			formik.setFieldValue(
				"phoneNumber",
				appData.user.mobileNo.toString(),
				false,
			);
		}
	}, [appData?.user?.mobileNo]);

	useEffect(() => {
		if (!open) {
			setTimeout(() => {
				setComponentState("form");
				setCurrentStep("phone");
				setApiError("");
				setResendTimerActive(false);
				setResendTimerCount(60);
				formik.resetForm({
					values: {
						phoneNumber: appData?.user?.mobileNo
							? appData.user.mobileNo.toString()
							: "",
						otp: "",
						message: "",
						captcha: null,
					},
				});
			}, 150);
		}
	}, [open, appData?.user?.mobileNo]);

	useEffect(() => {
		let interval: NodeJS.Timeout;
		if (resendTimerActive && resendTimerCount > 0) {
			interval = setInterval(() => setResendTimerCount((c) => c - 1), 1000);
		} else if (resendTimerCount === 0) {
			setResendTimerActive(false);
		}
		return () => clearInterval(interval);
	}, [resendTimerActive, resendTimerCount]);

	useEffect(() => {
		if (
			formik.values.captcha &&
			validateOtp(formik.values.otp) &&
			!formik.isSubmitting &&
			!formik.errors.otp &&
			currentStep === "otp"
		) {
			formik.setFieldTouched("otp", true, false);
			queueMicrotask(() => formik.submitForm());
		}
	}, [
		formik.values.captcha,
		formik.values.otp,
		formik.isSubmitting,
		formik.errors.otp,
		currentStep,
	]);

	// ── Product request form effects ─────────────────────────────────────────
	useEffect(() => {
		if (appData?.user?.mobileNo && productCurrentStep === "phone") {
			productFormik.setFieldValue(
				"phoneNumber",
				appData.user.mobileNo.toString(),
				false,
			);
		}
	}, [appData?.user?.mobileNo]);

	useEffect(() => {
		if (!openProductReq) {
			setTimeout(() => {
				setProductComponentState("form");
				setProductCurrentStep("phone");
				setProductApiError("");
				setProductResendTimerActive(false);
				setProductResendTimerCount(60);
				productFormik.resetForm({
					values: {
						phoneNumber: appData?.user?.mobileNo
							? appData.user.mobileNo.toString()
							: "",
						otp: "",
						message: "",
						captcha: null,
					},
				});
			}, 150);
		}
	}, [openProductReq, appData?.user?.mobileNo]);

	useEffect(() => {
		let interval: NodeJS.Timeout;
		if (productResendTimerActive && productResendTimerCount > 0) {
			interval = setInterval(
				() => setProductResendTimerCount((c) => c - 1),
				1000,
			);
		} else if (productResendTimerCount === 0) {
			setProductResendTimerActive(false);
		}
		return () => clearInterval(interval);
	}, [productResendTimerActive, productResendTimerCount]);

	useEffect(() => {
		if (
			productFormik.values.captcha &&
			validateOtp(productFormik.values.otp) &&
			!productFormik.isSubmitting &&
			!productFormik.errors.otp &&
			productCurrentStep === "otp"
		) {
			productFormik.setFieldTouched("otp", true, false);
			queueMicrotask(() => productFormik.submitForm());
		}
	}, [
		productFormik.values.captcha,
		productFormik.values.otp,
		productFormik.isSubmitting,
		productFormik.errors.otp,
		productCurrentStep,
	]);

	// ── Handlers ─────────────────────────────────────────────────────────────
	const handleCallbackClick = () => {
		faroManager.pushEvent("callback_button_clicked");
		setFabOpen(false);
		setOpen(true);
	};

	const handleProductReqClick = () => {
		faroManager.pushEvent("product_request_button_clicked");
		setFabOpen(false);
		setOpenProductReq(true);
	};

	const buildWhatsAppMessage = (): string => {
		const isProductPage = /^\/product\/[^/]+/.test(pathname ?? "");
		const slug = isProductPage
			? (params?.slug as string | undefined)
			: undefined;

		const formatProductName = (raw: string) =>
			decodeURIComponent(raw)
				.replace(/[;_-]/g, " ")
				.replace(/\s+/g, " ")
				.trim()
				.replace(/\b\w/g, (c) => c.toUpperCase());

		const deriveCompanyName = (email: string) => {
			const match = email.match(/@([^.]+)/);
			if (!match?.[1]) return "";
			return match[1]
				.split(/[\s._-]+/)
				.filter(Boolean)
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
				.join(" ");
		};

		const user = appData?.user;
		let name = user?.firstName?.trim() ?? "";
		if (user?.lastName) name += ` ${user?.lastName?.trim() ?? ""}`;
		const companyName = user?.employment?.email
			? deriveCompanyName(user.employment.email)
			: "";
		const productName = slug ? formatProductName(slug) : "";

		if (name) {
			const identity = companyName
				? `I am ${name} from ${companyName}`
				: `I am ${name}`;
			if (isProductPage && productName) {
				return `Hi, ${identity}.\nI have a query for ${productName} and would like to chat with someone at AutoMint.`;
			}
			return `Hi, ${identity}.\n\nI have a query and would like to chat with someone at AutoMint.`;
		}

		if (isProductPage && productName) {
			return `Hi,\nI have a query for ${productName} and would like to chat with someone at AutoMint.`;
		}
		return `Hi,\n\nI have a query and would like to chat with someone at AutoMint.`;
	};

	const handleWhatsAppClick = () => {
		faroManager.pushEvent("whatsapp_button_clicked");
		trackEvent(GTMEvent.CALLBACK_REQUESTED, { source: "whatsapp" });
		setFabOpen(false);
		const message = buildWhatsAppMessage();
		if (!WHATSAPP_URL) return;
		const url = `${WHATSAPP_URL}${encodeURIComponent(message)}`;
		if (isNativeApp) {
			window.location.href = url;
		} else {
			window.open(url, "_blank", "noopener,noreferrer");
		}
	};

	// ── OTP helpers – callback ────────────────────────────────────────────────
	const sendOtp = async (phoneNumber: string) => {
		try {
			const response = await getOtp(phoneNumber, "mobileNo");
			if (!response?.error) {
				setCurrentStep("otp");
				setApiError("");
				setResendTimerActive(true);
				setResendTimerCount(60);
				formik.setFieldError("phoneNumber", undefined);
			} else {
				formik.setFieldError(
					"phoneNumber",
					(response?.errorText as string) ||
						"Failed to send OTP. Please try again.",
				);
			}
		} catch (err) {
			formik.setFieldError(
				"phoneNumber",
				err instanceof Error
					? err.message
					: "An unexpected error occurred while sending OTP.",
			);
		}
	};

	const handleOtpSubmit = async (values: FormValues) => {
		try {
			const result = await verifyOtpForMobile(values.otp, values.captcha);
			if (
				result &&
				typeof result === "object" &&
				"success" in result &&
				result.success
			) {
				formik.setFieldError("otp", undefined);
				const callbackResponse = await requestCallback({
					number: values.phoneNumber,
					userNotes: values.message || undefined,
				});
				if ("error" in callbackResponse) {
					const errorResponse = callbackResponse as ErrorState;
					if (errorResponse.errorCode === 409) {
						trackEvent(GTMEvent.CALLBACK_REQUESTED, {
							source: effectiveSource,
						});
						clientLogger.info(
							"Callback request conflict (409) - treating as success",
							{
								phoneNumber: values.phoneNumber,
							},
						);
						setComponentState("success");
					} else {
						setComponentState("error");
						setApiError(
							errorResponse.errorText ||
								"Failed to request callback. Please try again.",
						);
					}
				} else {
					clientLogger.info(
						"Callback request successful after OTP verification",
						{
							callbackId: callbackResponse?.callbackId,
							userId: callbackResponse?.userId,
							phoneNumber: values.phoneNumber,
						},
					);
					trackEvent(GTMEvent.CALLBACK_REQUESTED, { source: effectiveSource });
					setComponentState("success");
				}
			} else {
				formik.setFieldError(
					"otp",
					"OTP verification failed. Please try again.",
				);
			}
		} catch (error) {
			formik.setFieldError(
				"otp",
				error instanceof Error
					? error.message
					: "An unexpected error occurred.",
			);
		}
	};

	const handleResendOtp = async () => {
		if (resendTimerActive || formik.isSubmitting) return;
		if (!formik.values.phoneNumber) {
			formik.setFieldError("otp", "Add a mobile number to request a new code.");
			return;
		}
		try {
			const response = await getOtp(formik.values.phoneNumber, "mobileNo");
			if (!response?.error) {
				setResendTimerActive(true);
				setResendTimerCount(60);
				formik.setFieldError("otp", undefined);
			} else {
				formik.setFieldError(
					"otp",
					(response?.errorText as string) ||
						"Failed to resend OTP. Please try again.",
				);
			}
		} catch (err) {
			formik.setFieldError(
				"otp",
				err instanceof Error
					? err.message
					: "An unexpected error occurred while resending OTP.",
			);
		}
	};

	const handleRetry = () => {
		setComponentState("form");
		setCurrentStep("phone");
		setApiError("");
		formik.resetForm({
			values: {
				phoneNumber: appData?.user?.mobileNo
					? appData.user.mobileNo.toString()
					: "",
				otp: "",
				message: "",
				captcha: null,
			},
		});
	};

	const handleClose = () => setOpen(false);

	// ── OTP helpers – product request ─────────────────────────────────────────
	const sendProductOtp = async (phoneNumber: string) => {
		try {
			const response = await getOtp(phoneNumber, "mobileNo");
			if (!response?.error) {
				setProductCurrentStep("otp");
				setProductApiError("");
				setProductResendTimerActive(true);
				setProductResendTimerCount(60);
				productFormik.setFieldError("phoneNumber", undefined);
			} else {
				productFormik.setFieldError(
					"phoneNumber",
					(response?.errorText as string) ||
						"Failed to send OTP. Please try again.",
				);
			}
		} catch (err) {
			productFormik.setFieldError(
				"phoneNumber",
				err instanceof Error
					? err.message
					: "An unexpected error occurred while sending OTP.",
			);
		}
	};

	const handleProductOtpSubmit = async (
		values: ProductFormValues,
		userNotes: string,
	) => {
		try {
			const result = await verifyOtpForMobile(values.otp, values.captcha);
			if (
				result &&
				typeof result === "object" &&
				"success" in result &&
				result.success
			) {
				productFormik.setFieldError("otp", undefined);
				const callbackResponse = await requestCallback({
					number: values.phoneNumber,
					userNotes,
				});
				if ("error" in callbackResponse) {
					const errorResponse = callbackResponse as ErrorState;
					if (errorResponse.errorCode === 409) {
						trackEvent(GTMEvent.CALLBACK_REQUESTED, {
							source: "request_product",
						});
						setProductComponentState("success");
					} else {
						setProductComponentState("error");
						setProductApiError(
							errorResponse.errorText ||
								"Failed to submit request. Please try again.",
						);
					}
				} else {
					trackEvent(GTMEvent.CALLBACK_REQUESTED, {
						source: "request_product",
					});
					setProductComponentState("success");
				}
			} else {
				productFormik.setFieldError(
					"otp",
					"OTP verification failed. Please try again.",
				);
			}
		} catch (error) {
			productFormik.setFieldError(
				"otp",
				error instanceof Error
					? error.message
					: "An unexpected error occurred.",
			);
		}
	};

	const handleProductResendOtp = async () => {
		if (productResendTimerActive || productFormik.isSubmitting) return;
		if (!productFormik.values.phoneNumber) {
			productFormik.setFieldError(
				"otp",
				"Add a mobile number to request a new code.",
			);
			return;
		}
		try {
			const response = await getOtp(
				productFormik.values.phoneNumber,
				"mobileNo",
			);
			if (!response?.error) {
				setProductResendTimerActive(true);
				setProductResendTimerCount(60);
				productFormik.setFieldError("otp", undefined);
			} else {
				productFormik.setFieldError(
					"otp",
					(response?.errorText as string) ||
						"Failed to resend OTP. Please try again.",
				);
			}
		} catch (err) {
			productFormik.setFieldError(
				"otp",
				err instanceof Error
					? err.message
					: "An unexpected error occurred while resending OTP.",
			);
		}
	};

	const handleProductRetry = () => {
		setProductComponentState("form");
		setProductCurrentStep("phone");
		setProductApiError("");
		productFormik.resetForm({
			values: {
				phoneNumber: appData?.user?.mobileNo
					? appData.user.mobileNo.toString()
					: "",
				otp: "",
				message: "",
				captcha: null,
			},
		});
	};

	const handleProductClose = () => setOpenProductReq(false);

	// ── Drawer renderers ──────────────────────────────────────────────────────
	function renderDrawerContent() {
		return (
			<DrawerContent
				background={"hsla(0, 0%, 100%, 1)"}
				style={{ colorScheme: "light" }}
				width={{ base: "90%", md: "560px" }}
				maxWidth={{ base: "400px", md: "560px" }}
				height={"auto"}
				marginX={"auto"}
				marginY={"auto"}
				padding={"12px"}
				rounded={"24px"}
				className="scrollbar-hide"
			>
				<DrawerHeader
					padding={"12px"}
					position={"relative"}
					display={"flex"}
					justifyContent={componentState === "success" ? "flex-end" : "between"}
				>
					{componentState === "error" && (
						<div className="flex items-center">
							<div className="h-16 w-16 rounded-full flex items-center justify-center bg-red-100">
								<Image
									src="/booking/unexpected.svg"
									alt="error icon"
									width={24}
									height={24}
									className="text-red-600"
								/>
							</div>
						</div>
					)}
					{componentState !== "success" && componentState !== "error" && (
						<p className="text-automint-text text-lg md:text-lg">
							Request call back
						</p>
					)}
					<DrawerCloseTrigger
						className={
							"bg-black text-white rounded-md p-2 flex items-center justify-center text-sm hover:bg-gray-800"
						}
						onClick={handleClose}
					>
						{componentState === "success" ? (
							<span>
								<svg
									width="20"
									height="20"
									viewBox="0 0 20 20"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									className="w-5 h-5 text-white"
									aria-hidden="true"
								>
									<path
										d="M6 6L14 14M14 6L6 14"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
									/>
								</svg>
							</span>
						) : (
							"X"
						)}
					</DrawerCloseTrigger>
				</DrawerHeader>
				<DrawerBody
					padding={"12px"}
					style={{ colorScheme: "light" }}
					className={cn(
						"scrollbar-hide",
						componentState === "error" && "text-automint-text text-center",
						componentState !== "success" &&
							componentState !== "error" &&
							"text-automint-text",
					)}
				>
					{componentState === "success" ? (
						<>
							<div className="flex justify-center mb-4">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="166"
									height="131"
									viewBox="0 0 166 131"
									fill="none"
									className="w-32 h-auto md:w-40"
								>
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M53.336 79.0546L53.3065 84.9887C53.3006 86.1808 53.513 87.2992 53.938 88.3349C54.3688 89.3824 55.0032 90.3266 55.8383 91.1617C56.6733 91.9968 57.6205 92.6312 58.6651 93.062C59.7008 93.4869 60.8192 93.6994 62.0113 93.6935L67.9454 93.664C68.6801 93.6581 69.3559 93.7879 69.9755 94.0447C70.5863 94.2955 71.1529 94.685 71.6722 95.2132L75.8476 99.4298C76.6856 100.277 77.6269 100.92 78.6597 101.351C79.6984 101.785 80.8138 102 82 102C83.1862 102 84.3016 101.782 85.3403 101.351C86.376 100.92 87.3144 100.28 88.1524 99.4298L92.3278 95.2132C92.8471 94.6909 93.4166 94.2985 94.0245 94.0447C94.6441 93.7879 95.3229 93.661 96.0546 93.664L101.989 93.6935C103.181 93.6994 104.299 93.4869 105.335 93.062C106.382 92.6312 107.327 91.9968 108.162 91.1617C108.997 90.3267 109.631 89.3795 110.062 88.3349C110.487 87.2992 110.699 86.1808 110.694 84.9887L110.664 79.0546C110.658 78.3199 110.788 77.6441 111.045 77.0245C111.295 76.4137 111.685 75.8471 112.213 75.3278L116.43 71.1524C117.277 70.3144 117.92 69.3731 118.351 68.3403C118.785 67.3016 119 66.1862 119 65C119 63.8138 118.782 62.6984 118.351 61.6597C117.92 60.624 117.28 59.6856 116.43 58.8475L112.213 54.6722C111.691 54.1529 111.298 53.5834 111.045 52.9755C110.788 52.3558 110.661 51.6771 110.664 50.9454L110.694 45.0113C110.699 43.8192 110.487 42.7008 110.062 41.6651C109.631 40.6176 108.997 39.6733 108.162 38.8382C107.327 38.0031 106.379 37.3688 105.335 36.938C104.299 36.513 103.181 36.3006 101.989 36.3065L96.0546 36.336C95.3199 36.3419 94.6412 36.2121 94.0245 35.9553C93.4137 35.7045 92.8471 35.315 92.3278 34.7868L88.1524 30.5702C87.3144 29.7233 86.3731 29.08 85.3403 28.6492C84.3016 28.2154 83.1862 28 82 28C80.8138 28 79.6984 28.2184 78.6597 28.6492C77.624 29.08 76.6856 29.7203 75.8476 30.5702L71.6722 34.7868C71.1529 35.3091 70.5834 35.7015 69.9755 35.9553C69.3559 36.2121 68.6771 36.3389 67.9454 36.336L62.0113 36.3065C60.8192 36.3006 59.7008 36.513 58.6651 36.938C57.6176 37.3688 56.6733 38.0032 55.8383 38.8382C55.0032 39.6733 54.3688 40.6205 53.938 41.6651C53.513 42.7008 53.3006 43.8192 53.3065 45.0113L53.336 50.9454C53.3419 51.6801 53.2121 52.3588 52.9553 52.9755C52.7045 53.5863 52.315 54.1529 51.7868 54.6722L47.5702 58.8475C46.7233 59.6856 46.08 60.6269 45.6492 61.6597C45.2154 62.6984 45 63.8138 45 65C45 66.1862 45.2184 67.3016 45.6492 68.3403C46.08 69.376 46.7203 70.3144 47.5702 71.1524L51.7868 75.3278C52.3091 75.8471 52.7015 76.4166 52.9553 77.0245C53.2121 77.6441 53.3389 78.3229 53.336 79.0546ZM97.2762 56.7967L78.4267 75.6462C77.748 76.3249 76.6532 76.3249 75.9746 75.6462L66.736 66.4077C66.0573 65.729 66.0573 64.6342 66.736 63.9556C67.4147 63.2769 68.5095 63.2769 69.1881 63.9556L77.2022 71.9696L94.8242 54.3476C95.5029 53.6689 96.5976 53.6689 97.2763 54.3476C97.955 55.0263 97.955 56.1211 97.2763 56.7997L97.2762 56.7967Z"
										fill="#005F5A"
									/>
									<path
										d="M145.188 31.8833C145.906 30.3444 148.094 30.3444 148.812 31.8833L150.829 36.2045C151.028 36.6301 151.37 36.9723 151.796 37.171L156.117 39.1877C157.656 39.9059 157.656 42.0941 156.117 42.8123L151.796 44.829C151.37 45.0277 151.028 45.3699 150.829 45.7955L148.812 50.1167C148.094 51.6556 145.906 51.6556 145.188 50.1167L143.171 45.7955C142.972 45.3699 142.63 45.0277 142.204 44.829L137.883 42.8123C136.344 42.0941 136.344 39.9059 137.883 39.1877L142.204 37.171C142.63 36.9723 142.972 36.6301 143.171 36.2045L145.188 31.8833Z"
										fill="#005F5A"
									/>
									<path
										d="M22.0967 89.4696C22.6784 86.9355 26.3216 86.9355 26.9033 89.4696C27.1068 90.3563 27.8134 91.0519 28.7054 91.2311C31.1833 91.7289 31.1833 95.2711 28.7054 95.7689C27.8134 95.9481 27.1068 96.6437 26.9033 97.5304C26.3216 100.064 22.6784 100.064 22.0967 97.5304C21.8932 96.6437 21.1866 95.9481 20.2946 95.7689C17.8167 95.2711 17.8167 91.7289 20.2946 91.2311C21.1866 91.0519 21.8932 90.3563 22.0967 89.4696Z"
										fill="#005F5A"
									/>
									<circle cx="45.5" cy="17.5" r="3.5" fill="#005F5A" />
									<circle cx="116.5" cy="3.5" r="3.5" fill="#005F5A" />
									<circle cx="123.5" cy="116.5" r="3.5" fill="#005F5A" />
									<circle cx="164" cy="99.5" r="1.5" fill="#005F5A" />
									<circle cx="47" cy="129" r="2" fill="#005F5A" />
									<circle cx="1.5" cy="42.5" r="1.5" fill="#005F5A" />
								</svg>
							</div>
							<h3 className="text-automint-text text-base text-center mb-2">
								We&apos;ve received your callback request
							</h3>
							<p className="mt-2.5 text-text-two text-sm">
								Our team will contact you shortly on{" "}
								<span className="text-automint-text text-sm">
									+91 {appData?.user?.mobileNo || formik.values.phoneNumber}
								</span>
							</p>
							<p className="mt-2 text-sm text-gray-600">
								We are available at 9AM - 7PM
							</p>
							<Button
								variant="primary"
								size="md"
								width="full"
								onClick={handleClose}
								className="mt-6 mb-2 w-full block"
							>
								Great!
							</Button>
						</>
					) : componentState === "error" ? (
						<>
							<h3 className="mt-2.5 text-center text-automint-app-title text-red-600">
								Request Failed
							</h3>
							<p className="mt-2.5 text-center text-automint-app-body">
								{apiError}
							</p>
							<div className="flex gap-3 mt-6 mb-4">
								<Button
									variant="secondary"
									size="md"
									width="full"
									onClick={handleClose}
									className="w-full block"
								>
									Cancel
								</Button>
								<Button
									variant="primary"
									size="md"
									width="full"
									onClick={handleRetry}
									className="w-full block"
								>
									Try Again
								</Button>
							</div>
						</>
					) : (
						<form onSubmit={formik.handleSubmit} noValidate>
							{appData?.user && appData?.user?.mobileNo ? (
								<>
									<div className="py-5">
										<p className="text-automint-app-body text-left">
											Enter your contact number.
										</p>
										<p className="text-automint-app-subtext text-text-two text-left mt-1">
											We are available 9 AM - 7 PM.
										</p>
									</div>
									<div className="mt-1">
										<Input
											name="phoneNumber"
											value={appData.user.mobileNo.toString()}
											onChange={() => {}}
											onBlur={() => {}}
											label="Contact number"
											variant="static"
											required
											startAdornment={
												<MdPhoneIphone
													className="text-button-text-link"
													size={20}
												/>
											}
											disabled={true}
										/>
									</div>
									<div className="mt-4">
										<label
											htmlFor="message"
											className="block text-sm font-medium text-automint-text mb-1.5"
										>
											Message (Optional)
										</label>
										<textarea
											id="message"
											name="message"
											rows={4}
											maxLength={500}
											placeholder="Enter your message"
											value={formik.values.message}
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											disabled={formik.isSubmitting}
											className="w-full px-3 py-2 rounded-md resize-none text-sm bg-white text-black border border-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-automint-neon focus:border-transparent dark:bg-white dark:text-black dark:placeholder:text-gray-400"
											style={{ colorScheme: "light" }}
										/>
										<div className="flex justify-end mt-1">
											<span className="text-xs text-gray-500">
												{formik.values.message.length}/500
											</span>
										</div>
									</div>
								</>
							) : (
								<>
									{currentStep === "phone" ? (
										<>
											<div className="py-5">
												<p className="text-automint-app-body text-left">
													Enter your contact number.
												</p>
												<p className="text-automint-app-subtext text-text-two text-left mt-1">
													We are available 9 AM - 7 PM.
												</p>
											</div>
											<div className="mt-2.5">
												<Input
													name="phoneNumber"
													value={formik.values.phoneNumber}
													onChange={(e) => {
														const digitsOnly = e.target.value
															.replace(/\D/g, "")
															.slice(0, 10);
														formik.setFieldValue(
															"phoneNumber",
															digitsOnly,
															true,
														);
														if (!formik.touched.phoneNumber)
															formik.setFieldTouched(
																"phoneNumber",
																true,
																false,
															);
													}}
													onBlur={formik.handleBlur}
													label="Contact number"
													placeholder="9876543210"
													type="tel"
													inputMode="numeric"
													autoComplete="tel"
													required
													errorText={
														formik.touched.phoneNumber
															? formik.errors.phoneNumber
															: undefined
													}
													startAdornment={
														<MdPhoneIphone
															className={
																formik.touched.phoneNumber &&
																formik.errors.phoneNumber
																	? "text-automint-error"
																	: "text-button-text-link"
															}
															size={20}
														/>
													}
													disabled={formik.isSubmitting}
												/>
											</div>
											<div className="mt-4">
												<label
													htmlFor="message"
													className="block text-sm font-medium text-automint-text mb-1.5"
												>
													Message (Optional)
												</label>
												<textarea
													id="message"
													name="message"
													rows={4}
													maxLength={500}
													placeholder="Enter your message"
													value={formik.values.message}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-automint-neon focus:border-transparent resize-none text-sm"
													disabled={formik.isSubmitting}
												/>
												<div className="flex justify-end mt-1">
													<span className="text-xs text-gray-500">
														{formik.values.message.length}/500
													</span>
												</div>
											</div>
										</>
									) : (
										<>
											<div className="mt-2.5 text-text-two text-sm">
												<p>
													Enter OTP sent to{" "}
													<span className="text-automint-text">
														{formik.values.phoneNumber}
													</span>
												</p>
											</div>
											<div className="mt-2.5">
												<OTPInput
													length={5}
													label="Verification code"
													required
													value={formik.values.otp}
													onChange={(otpValue) =>
														formik.setFieldValue("otp", otpValue, true)
													}
													onComplete={(completedOtp) => {
														if (
															validateOtp(completedOtp) &&
															!formik.isSubmitting &&
															formik.values.captcha
														) {
															formik.setFieldTouched("otp", true, false);
															queueMicrotask(() => formik.submitForm());
														}
													}}
													errorText={
														formik.touched.otp ? formik.errors.otp : undefined
													}
													autoFocus={true}
												/>
												<div className="flex justify-between items-center mt-3">
													<p className="text-sm text-gray-600">
														Didn&apos;t receive a code?
													</p>
													<Button
														variant="link"
														size="sm"
														width="inline"
														onClick={handleResendOtp}
														disabled={resendTimerActive || formik.isSubmitting}
													>
														{resendTimerActive
															? `Resend in ${resendTimerCount}s`
															: "Resend OTP"}
													</Button>
												</div>
												{validateOtp(formik.values.otp) && (
													<div className="mt-4">
														<Turnstile
															siteKey={SITE_KEY}
															onSuccess={(token) =>
																formik.setFieldValue("captcha", token)
															}
															onError={() =>
																formik.setFieldValue("captcha", null)
															}
															onExpire={() =>
																formik.setFieldValue("captcha", null)
															}
															options={{
																theme: "light",
																appearance: "interaction-only",
															}}
														/>
													</div>
												)}
											</div>
										</>
									)}
								</>
							)}
							<Button
								type="submit"
								variant="primary"
								size="md"
								width="full"
								loading={formik.isSubmitting}
								disabled={
									formik.isSubmitting ||
									(currentStep === "phone" &&
										!appData?.user?.mobileNo &&
										!validatePhone(formik.values.phoneNumber || "")) ||
									(currentStep === "otp" &&
										(!formik.values.otp ||
											!validateOtp(formik.values.otp) ||
											!formik.values.captcha))
								}
								className="mt-4 mb-2 px-4 py-2.5 text-center"
							>
								<div className="w-full flex justify-center items-center">
									{formik.isSubmitting
										? "Processing..."
										: currentStep === "phone" && !appData?.user?.mobileNo
											? "Send OTP"
											: currentStep === "otp"
												? "Verify & Request"
												: "Request"}
								</div>
							</Button>
						</form>
					)}
				</DrawerBody>
			</DrawerContent>
		);
	}

	function renderProductDrawerContent() {
		return (
			<DrawerContent
				background={"hsla(0, 0%, 100%, 1)"}
				style={{ colorScheme: "light" }}
				width={{ base: "90%", md: "560px" }}
				maxWidth={{ base: "400px", md: "560px" }}
				height={"auto"}
				marginX={"auto"}
				marginY={"auto"}
				padding={"12px"}
				rounded={"24px"}
				className="scrollbar-hide"
			>
				<DrawerHeader
					padding={"12px"}
					position={"relative"}
					display={"flex"}
					justifyContent={
						productComponentState === "success" ? "flex-end" : "between"
					}
				>
					{productComponentState === "error" && (
						<div className="flex items-center">
							<div className="h-16 w-16 rounded-full flex items-center justify-center bg-red-100">
								<Image
									src="/booking/unexpected.svg"
									alt="error icon"
									width={24}
									height={24}
									className="text-red-600"
								/>
							</div>
						</div>
					)}
					{productComponentState !== "success" &&
						productComponentState !== "error" && (
							<p className="text-automint-text text-lg md:text-lg">
								Request a new product
							</p>
						)}
					<DrawerCloseTrigger
						className={
							"bg-black text-white rounded-md p-2 flex items-center justify-center text-sm hover:bg-gray-800"
						}
						onClick={handleProductClose}
					>
						{productComponentState === "success" ? (
							<span>
								<svg
									width="20"
									height="20"
									viewBox="0 0 20 20"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									className="w-5 h-5 text-white"
									aria-hidden="true"
								>
									<path
										d="M6 6L14 14M14 6L6 14"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
									/>
								</svg>
							</span>
						) : (
							"X"
						)}
					</DrawerCloseTrigger>
				</DrawerHeader>
				<DrawerBody
					padding={"12px"}
					style={{ colorScheme: "light" }}
					className={cn(
						"scrollbar-hide",
						productComponentState === "error" &&
							"text-automint-text text-center",
						productComponentState !== "success" &&
							productComponentState !== "error" &&
							"text-automint-text",
					)}
				>
					{productComponentState === "success" ? (
						<>
							<div className="flex justify-center mb-4">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="166"
									height="131"
									viewBox="0 0 166 131"
									fill="none"
									className="w-32 h-auto md:w-40"
								>
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M53.336 79.0546L53.3065 84.9887C53.3006 86.1808 53.513 87.2992 53.938 88.3349C54.3688 89.3824 55.0032 90.3266 55.8383 91.1617C56.6733 91.9968 57.6205 92.6312 58.6651 93.062C59.7008 93.4869 60.8192 93.6994 62.0113 93.6935L67.9454 93.664C68.6801 93.6581 69.3559 93.7879 69.9755 94.0447C70.5863 94.2955 71.1529 94.685 71.6722 95.2132L75.8476 99.4298C76.6856 100.277 77.6269 100.92 78.6597 101.351C79.6984 101.785 80.8138 102 82 102C83.1862 102 84.3016 101.782 85.3403 101.351C86.376 100.92 87.3144 100.28 88.1524 99.4298L92.3278 95.2132C92.8471 94.6909 93.4166 94.2985 94.0245 94.0447C94.6441 93.7879 95.3229 93.661 96.0546 93.664L101.989 93.6935C103.181 93.6994 104.299 93.4869 105.335 93.062C106.382 92.6312 107.327 91.9968 108.162 91.1617C108.997 90.3267 109.631 89.3795 110.062 88.3349C110.487 87.2992 110.699 86.1808 110.694 84.9887L110.664 79.0546C110.658 78.3199 110.788 77.6441 111.045 77.0245C111.295 76.4137 111.685 75.8471 112.213 75.3278L116.43 71.1524C117.277 70.3144 117.92 69.3731 118.351 68.3403C118.785 67.3016 119 66.1862 119 65C119 63.8138 118.782 62.6984 118.351 61.6597C117.92 60.624 117.28 59.6856 116.43 58.8475L112.213 54.6722C111.691 54.1529 111.298 53.5834 111.045 52.9755C110.788 52.3558 110.661 51.6771 110.664 50.9454L110.694 45.0113C110.699 43.8192 110.487 42.7008 110.062 41.6651C109.631 40.6176 108.997 39.6733 108.162 38.8382C107.327 38.0031 106.379 37.3688 105.335 36.938C104.299 36.513 103.181 36.3006 101.989 36.3065L96.0546 36.336C95.3199 36.3419 94.6412 36.2121 94.0245 35.9553C93.4137 35.7045 92.8471 35.315 92.3278 34.7868L88.1524 30.5702C87.3144 29.7233 86.3731 29.08 85.3403 28.6492C84.3016 28.2154 83.1862 28 82 28C80.8138 28 79.6984 28.2184 78.6597 28.6492C77.624 29.08 76.6856 29.7203 75.8476 30.5702L71.6722 34.7868C71.1529 35.3091 70.5834 35.7015 69.9755 35.9553C69.3559 36.2121 68.6771 36.3389 67.9454 36.336L62.0113 36.3065C60.8192 36.3006 59.7008 36.513 58.6651 36.938C57.6176 37.3688 56.6733 38.0032 55.8383 38.8382C55.0032 39.6733 54.3688 40.6205 53.938 41.6651C53.513 42.7008 53.3006 43.8192 53.3065 45.0113L53.336 50.9454C53.3419 51.6801 53.2121 52.3588 52.9553 52.9755C52.7045 53.5863 52.315 54.1529 51.7868 54.6722L47.5702 58.8475C46.7233 59.6856 46.08 60.6269 45.6492 61.6597C45.2154 62.6984 45 63.8138 45 65C45 66.1862 45.2184 67.3016 45.6492 68.3403C46.08 69.376 46.7203 70.3144 47.5702 71.1524L51.7868 75.3278C52.3091 75.8471 52.7015 76.4166 52.9553 77.0245C53.2121 77.6441 53.3389 78.3229 53.336 79.0546ZM97.2762 56.7967L78.4267 75.6462C77.748 76.3249 76.6532 76.3249 75.9746 75.6462L66.736 66.4077C66.0573 65.729 66.0573 64.6342 66.736 63.9556C67.4147 63.2769 68.5095 63.2769 69.1881 63.9556L77.2022 71.9696L94.8242 54.3476C95.5029 53.6689 96.5976 53.6689 97.2763 54.3476C97.955 55.0263 97.955 56.1211 97.2763 56.7997L97.2762 56.7967Z"
										fill="#005F5A"
									/>
									<path
										d="M145.188 31.8833C145.906 30.3444 148.094 30.3444 148.812 31.8833L150.829 36.2045C151.028 36.6301 151.37 36.9723 151.796 37.171L156.117 39.1877C157.656 39.9059 157.656 42.0941 156.117 42.8123L151.796 44.829C151.37 45.0277 151.028 45.3699 150.829 45.7955L148.812 50.1167C148.094 51.6556 145.906 51.6556 145.188 50.1167L143.171 45.7955C142.972 45.3699 142.63 45.0277 142.204 44.829L137.883 42.8123C136.344 42.0941 136.344 39.9059 137.883 39.1877L142.204 37.171C142.63 36.9723 142.972 36.6301 143.171 36.2045L145.188 31.8833Z"
										fill="#005F5A"
									/>
									<path
										d="M22.0967 89.4696C22.6784 86.9355 26.3216 86.9355 26.9033 89.4696C27.1068 90.3563 27.8134 91.0519 28.7054 91.2311C31.1833 91.7289 31.1833 95.2711 28.7054 95.7689C27.8134 95.9481 27.1068 96.6437 26.9033 97.5304C26.3216 100.064 22.6784 100.064 22.0967 97.5304C21.8932 96.6437 21.1866 95.9481 20.2946 95.7689C17.8167 95.2711 17.8167 91.7289 20.2946 91.2311C21.1866 91.0519 21.8932 90.3563 22.0967 89.4696Z"
										fill="#005F5A"
									/>
									<circle cx="45.5" cy="17.5" r="3.5" fill="#005F5A" />
									<circle cx="116.5" cy="3.5" r="3.5" fill="#005F5A" />
									<circle cx="123.5" cy="116.5" r="3.5" fill="#005F5A" />
									<circle cx="164" cy="99.5" r="1.5" fill="#005F5A" />
									<circle cx="47" cy="129" r="2" fill="#005F5A" />
									<circle cx="1.5" cy="42.5" r="1.5" fill="#005F5A" />
								</svg>
							</div>
							<h3 className="text-automint-text text-base text-center mb-2">
								We&apos;ve received your product request!
							</h3>
							<p className="mt-2.5 text-text-two text-sm">
								Our team will get back to you on{" "}
								<span className="text-automint-text text-sm">
									+91{" "}
									{appData?.user?.mobileNo || productFormik.values.phoneNumber}
								</span>
							</p>
							<p className="mt-2 text-sm text-gray-600">
								We are available at 9AM - 7PM
							</p>
							<Button
								variant="primary"
								size="md"
								width="full"
								onClick={handleProductClose}
								className="mt-6 mb-2 w-full block"
							>
								Great!
							</Button>
						</>
					) : productComponentState === "error" ? (
						<>
							<h3 className="mt-2.5 text-center text-automint-app-title text-red-600">
								Request Failed
							</h3>
							<p className="mt-2.5 text-center text-automint-app-body">
								{productApiError}
							</p>
							<div className="flex gap-3 mt-6 mb-4">
								<Button
									variant="secondary"
									size="md"
									width="full"
									onClick={handleProductClose}
									className="w-full block"
								>
									Cancel
								</Button>
								<Button
									variant="primary"
									size="md"
									width="full"
									onClick={handleProductRetry}
									className="w-full block"
								>
									Try Again
								</Button>
							</div>
						</>
					) : (
						<form onSubmit={productFormik.handleSubmit} noValidate>
							{productCurrentStep === "phone" && (
								<>
									<div className="py-5">
										<p className="text-automint-app-body text-left">
											Tell us what you&apos;re looking for.
										</p>
										<p className="text-automint-app-subtext text-text-two text-left mt-1">
											We are available 9 AM - 7 PM.
										</p>
									</div>

									{appData?.user && appData?.user?.mobileNo ? (
										<div className="mt-1">
											<Input
												name="phoneNumber"
												value={appData.user.mobileNo.toString()}
												onChange={() => {}}
												onBlur={() => {}}
												label="Contact number"
												variant="static"
												required
												startAdornment={
													<MdPhoneIphone
														className="text-button-text-link"
														size={20}
													/>
												}
												disabled={true}
											/>
										</div>
									) : (
										<div className="mt-1">
											<Input
												name="phoneNumber"
												value={productFormik.values.phoneNumber}
												onChange={(e) => {
													const digitsOnly = e.target.value
														.replace(/\D/g, "")
														.slice(0, 10);
													productFormik.setFieldValue(
														"phoneNumber",
														digitsOnly,
														true,
													);
													if (!productFormik.touched.phoneNumber)
														productFormik.setFieldTouched(
															"phoneNumber",
															true,
															false,
														);
												}}
												onBlur={productFormik.handleBlur}
												label="Contact number"
												placeholder="9876543210"
												type="tel"
												inputMode="numeric"
												autoComplete="tel"
												required
												errorText={
													productFormik.touched.phoneNumber
														? productFormik.errors.phoneNumber
														: undefined
												}
												startAdornment={
													<MdPhoneIphone
														className={
															productFormik.touched.phoneNumber &&
															productFormik.errors.phoneNumber
																? "text-automint-error"
																: "text-button-text-link"
														}
														size={20}
													/>
												}
												disabled={productFormik.isSubmitting}
											/>
										</div>
									)}

									<div className="mt-4">
										<textarea
											id="product-message"
											name="message"
											rows={4}
											maxLength={500}
											placeholder="Tell us what product you’re looking for and we’ll do our best to make it available for you."
											value={productFormik.values.message}
											onChange={productFormik.handleChange}
											onBlur={productFormik.handleBlur}
											disabled={productFormik.isSubmitting}
											className="w-full px-3 py-2 rounded-md resize-none text-sm bg-white text-black border border-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-automint-neon focus:border-transparent dark:bg-white dark:text-black dark:placeholder:text-gray-400"
											style={{ colorScheme: "light" }}
										/>
										{productFormik.touched.message &&
											productFormik.errors.message && (
												<p className="mt-1 text-xs text-automint-error">
													{productFormik.errors.message}
												</p>
											)}
										<div className="flex justify-end mt-1">
											<span className="text-xs text-gray-500">
												{productFormik.values.message.length}/500
											</span>
										</div>
									</div>
								</>
							)}

							{productCurrentStep === "otp" && (
								<div className="mt-4">
									<div className="mb-2 text-text-two text-sm">
										<p>
											Enter OTP sent to{" "}
											<span className="text-automint-text">
												{productFormik.values.phoneNumber}
											</span>
										</p>
									</div>
									<OTPInput
										length={5}
										label="Verification code"
										required
										value={productFormik.values.otp}
										onChange={(otpValue) =>
											productFormik.setFieldValue("otp", otpValue, true)
										}
										onComplete={(completedOtp) => {
											if (
												validateOtp(completedOtp) &&
												!productFormik.isSubmitting &&
												productFormik.values.captcha
											) {
												productFormik.setFieldTouched("otp", true, false);
												queueMicrotask(() => productFormik.submitForm());
											}
										}}
										errorText={
											productFormik.touched.otp
												? productFormik.errors.otp
												: undefined
										}
										autoFocus={true}
									/>
									<div className="flex justify-between items-center mt-3">
										<p className="text-sm text-gray-600">
											Didn&apos;t receive a code?
										</p>
										<Button
											variant="link"
											size="sm"
											width="inline"
											onClick={handleProductResendOtp}
											disabled={
												productResendTimerActive || productFormik.isSubmitting
											}
										>
											{productResendTimerActive
												? `Resend in ${productResendTimerCount}s`
												: "Resend OTP"}
										</Button>
									</div>
									{validateOtp(productFormik.values.otp) && (
										<div className="mt-4">
											<Turnstile
												siteKey={SITE_KEY}
												onSuccess={(token) =>
													productFormik.setFieldValue("captcha", token)
												}
												onError={() =>
													productFormik.setFieldValue("captcha", null)
												}
												onExpire={() =>
													productFormik.setFieldValue("captcha", null)
												}
												options={{
													theme: "light",
													appearance: "interaction-only",
												}}
											/>
										</div>
									)}
								</div>
							)}

							<Button
								type="submit"
								variant="primary"
								size="md"
								width="full"
								loading={productFormik.isSubmitting}
								disabled={
									productFormik.isSubmitting ||
									!productFormik.values.message.trim() ||
									(productCurrentStep === "phone" &&
										!appData?.user?.mobileNo &&
										!validatePhone(productFormik.values.phoneNumber || "")) ||
									(productCurrentStep === "otp" &&
										(!productFormik.values.otp ||
											!validateOtp(productFormik.values.otp) ||
											!productFormik.values.captcha))
								}
								className="mt-4 mb-2 px-4 py-2.5 text-center"
							>
								<div className="w-full flex justify-center items-center">
									{productFormik.isSubmitting
										? "Processing..."
										: productCurrentStep === "phone" && !appData?.user?.mobileNo
											? "Send OTP"
											: productCurrentStep === "otp"
												? "Verify & Submit"
												: "Submit Request"}
								</div>
							</Button>
						</form>
					)}
				</DrawerBody>
			</DrawerContent>
		);
	}

	if (type !== "default") {
		return (
			<DrawerRoot
				open={open}
				onOpenChange={(details) => setOpen(details.open)}
				placement={"bottom"}
				size={"full"}
			>
				<DrawerTrigger display={"flex"} onClick={handleCallbackClick} asChild>
					<p className="text-autoomint-black text-lg">Request a callback</p>
				</DrawerTrigger>
				{renderDrawerContent()}
			</DrawerRoot>
		);
	}

	return (
		<>
			{/* Backdrop to close FAB when clicking outside */}
			{fabOpen && (
				<div
					className="fixed inset-0 z-40"
					onClick={() => setFabOpen(false)}
					aria-hidden="true"
				/>
			)}

			{/* Background blur when any drawer is open */}
			{(open || openProductReq) && (
				<div
					className="fixed inset-0 z-[45] backdrop-blur-sm bg-black/10"
					aria-hidden="true"
					style={{ pointerEvents: "none" }}
				/>
			)}

			{/* FAB container */}
			<div
				className={cn(
					"flex flex-col items-end gap-3",
					embedded && "pointer-events-none",
					!embedded && "fixed right-4 md:right-16 bottom-28 md:bottom-20 z-50",
				)}
			>
				{/* Speed-dial sub-actions */}
				<div
					className={cn(
						"relative z-50 flex flex-col items-end gap-3 transition-all duration-200",
						fabOpen
							? "opacity-100 translate-y-0 pointer-events-auto"
							: "opacity-0 translate-y-4 pointer-events-none",
					)}
				>
					{/* WhatsApp action */}
					<div className="flex items-center gap-2">
						<span className="bg-white text-automint-text text-xs font-medium px-3 py-1.5 rounded-full shadow-md whitespace-nowrap">
							WhatsApp
						</span>
						<button
							type="button"
							onClick={handleWhatsAppClick}
							aria-label="Chat on WhatsApp"
							className="hover:opacity-90 transition-opacity"
						>
							<Image
								src="/new_icons/general/whatsapp_black.svg"
								alt="WhatsApp"
								width={54}
								height={54}
							/>
						</button>
					</div>

					{/* Request Product action */}
					<DrawerRoot
						open={openProductReq}
						onOpenChange={(details) => setOpenProductReq(details.open)}
						placement={"bottom"}
						size={"full"}
					>
						<div className="flex items-center gap-2">
							<span className="bg-white text-automint-text text-xs font-medium px-3 py-1.5 rounded-full shadow-md whitespace-nowrap">
								Request product
							</span>
							<DrawerTrigger
								display={"flex"}
								onClick={handleProductReqClick}
								asChild
							>
								<button
									type="button"
									aria-label="Request product"
									className="hover:opacity-90 transition-opacity"
								>
									<Image
										src="/new_icons/req-product/req-product.svg"
										alt="Request Product"
										width={54}
										height={54}
									/>
								</button>
							</DrawerTrigger>
						</div>
						{renderProductDrawerContent()}
					</DrawerRoot>

					{/* Request Callback action */}
					<DrawerRoot
						open={open}
						onOpenChange={(details) => setOpen(details.open)}
						placement={"bottom"}
						size={"full"}
					>
						<div className="flex items-center gap-2">
							<span className="bg-white text-automint-text text-xs font-medium px-3 py-1.5 rounded-full shadow-md whitespace-nowrap">
								Request Callback
							</span>
							<DrawerTrigger
								display={"flex"}
								onClick={handleCallbackClick}
								asChild
							>
								<button
									type="button"
									aria-label="Request callback"
									className="hover:opacity-90 transition-opacity"
								>
									<Image
										src="/new_icons/general/phone_green.svg"
										alt="Request Callback"
										width={54}
										height={54}
									/>
								</button>
							</DrawerTrigger>
						</div>
						{renderDrawerContent()}
					</DrawerRoot>
				</div>

				{/* Main FAB toggle button */}
				<button
					type="button"
					onClick={() => setFabOpen((prev) => !prev)}
					aria-label={
						fabOpen ? "Close contact options" : "Open contact options"
					}
					aria-expanded={fabOpen}
					className={cn(
						"-mr-5 transition-opacity",
						embedded
							? active
								? "pointer-events-auto"
								: "pointer-events-none"
							: "",
					)}
				>
					<div className="relative w-[80px] h-[82px]">
						<Image
							src="/new_icons/general/whatsapp_green.svg"
							alt="Contact us"
							width={80}
							height={82}
							className={cn(
								"absolute inset-0 transition-all duration-300",
								fabOpen ? "opacity-0 rotate-90" : "opacity-100 rotate-0",
							)}
						/>
						<Image
							src="/new_icons/general/fab_close.svg"
							alt="Close"
							width={80}
							height={82}
							className={cn(
								"absolute inset-0 transition-all duration-300",
								fabOpen ? "opacity-100 rotate-0" : "opacity-0 -rotate-90",
							)}
						/>
					</div>
				</button>
			</div>
		</>
	);
};

export default Callback;
